import React, { ChangeEvent, useRef, useState } from "react";
import { trimStrings } from "@pulse-ui/formatting";

// Type Imports
import {
  IAlertDestinations,
  IGroupDestinations,
} from "../../AlertManager.types";

// RTK Query
import {
  useFetchGroupDestinationsQuery,
  useCreateGroupDestinationMutation,
} from "../../services/alertManagerApi";

// Hooks
import { useAlerts } from "@/hooks";
import { getErrorMessage } from "@/utils/getErrorMessage";

// MUI Imports
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import TelegramIcon from "@mui/icons-material/Telegram";
import TagIcon from "@mui/icons-material/Tag";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";

import { v4 as uuidv4 } from "uuid";

// --- Sub-hook: destination item being built ---
interface INewGroupDestinationForm {
  group_name: string;
  alert_dest_list: IAlertDestinations[];
}

interface INewGroupDestinationFormErrors {
  group_name: boolean;
  alert_dest_list: boolean;
}

const EMPTY_DESTINATION: IAlertDestinations = { alert_dest_type: "", value: "" };
const EMPTY_FORM: INewGroupDestinationForm = { group_name: "", alert_dest_list: [] };

function useDestinationItem() {
  const [newDestination, setNewDestination] =
    useState<IAlertDestinations>(EMPTY_DESTINATION);

  const saveButtonDisabled =
    newDestination.alert_dest_type.length === 0 ||
    newDestination.value.length === 0;

  const handleNewDestinationChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewDestination((prev) => ({ ...prev, [name]: value }));
  };

  const reset = () => setNewDestination(EMPTY_DESTINATION);

  return {
    newDestination,
    saveButtonDisabled,
    handleNewDestinationChange,
    reset,
  };
}

function useGroupDestinationForm(
  destinationItem: IAlertDestinations,
  resetDestinationItem: () => void
) {
  const { showSuccess, showError } = useAlerts();
  const [form, setForm] = useState<INewGroupDestinationForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<INewGroupDestinationFormErrors>({
    group_name: false,
    alert_dest_list: false,
  });
  const [createGroupDestination, { isLoading: pending }] =
    useCreateGroupDestinationMutation();

  const handleFormChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddDestination = () => {
    setForm((prev) => ({
      ...prev,
      alert_dest_list: [...prev.alert_dest_list, destinationItem],
    }));
    resetDestinationItem();
  };

  const handleDeleteDestination = (idx: number) => {
    setForm((prev) => ({
      ...prev,
      alert_dest_list: prev.alert_dest_list.filter((_, i) => i !== idx),
    }));
  };

  const validate = (): boolean => {
    const newErrors: INewGroupDestinationFormErrors = {
      group_name: form.group_name.length < 1,
      alert_dest_list: form.alert_dest_list.length < 1,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      await createGroupDestination(trimStrings(form) as any).unwrap();
      showSuccess("Destination Group saved");
      setForm(EMPTY_FORM);
      setErrors({ group_name: false, alert_dest_list: false });
    } catch (err) {
      showError(`Failed to save Destination Group: ${getErrorMessage(err)}`);
    }
  };

  const loadGroup = (group: IGroupDestinations) => {
    setForm({ group_name: group.group_name, alert_dest_list: group.alert_dest_list ?? [] });
  };

  return {
    form,
    errors,
    pending,
    handleFormChange,
    handleAddDestination,
    handleDeleteDestination,
    handleSave,
    loadGroup,
  };
}

function useGroupEditAccordion(
  loadGroup: (group: IGroupDestinations) => void,
  data: IGroupDestinations[]
) {
  const [accordionExpanded, setAccordionExpanded] = useState(true);
  const accordionRef = useRef<HTMLDivElement | null>(null);

  const handleEditGroupDestination = (group_name: string) => {
    const found = data.find((d) => d.group_name === group_name);
    if (found) {
      loadGroup(found);
      if (accordionRef.current) {
        accordionRef.current.scrollIntoView({ behavior: "smooth" });
        setTimeout(() => {
          if (accordionRef.current) {
            if (!accordionExpanded) setAccordionExpanded(true);
            accordionRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      }
    }
  };

  return { accordionExpanded, setAccordionExpanded, accordionRef, handleEditGroupDestination };
}

// --- Styled components ---
const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(2) },
  "& .MuiDialogActions-root": { padding: theme.spacing(1) },
}));

export interface DialogTitleProps {
  className: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function CustomDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;
  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose && (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#ebe93e" }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </DialogTitle>
  );
}

// --- Destination type icon helper ---
function DestinationIcon({ type }: { type: string }) {
  if (type === "sms") return <ChatBubbleOutlineIcon />;
  if (type === "slack") return <TagIcon />;
  if (type === "telegram") return <TelegramIcon />;
  return <AppsOutlinedIcon />;
}

export interface IAlertFormType {
  label: string;
  value: string;
}

const ALERT_DESTINATION_TYPES: IAlertFormType[] = [
  { label: "Slack", value: "slack" },
  { label: "Telegram", value: "telegram" },
  { label: "SMS", value: "sms" },
  { label: "Group", value: "group" },
];

// --- Props ---
interface IProps {
  open: boolean;
  close: () => void;
  slackChannels: string[];
  telegramChannels: string[];
  smsChannels: string[];
}

const GroupDestinationsModal = ({
  open,
  close,
  slackChannels,
  telegramChannels,
  smsChannels,
}: IProps) => {
  const { data: groupDestinations } = useFetchGroupDestinationsQuery();
  const data: IGroupDestinations[] = groupDestinations ?? [];
  const groupDestinationNames = data.map((d) => d.group_name);

  const destinationItem = useDestinationItem();
  const groupForm = useGroupDestinationForm(
    destinationItem.newDestination,
    destinationItem.reset
  );
  const accordion = useGroupEditAccordion(groupForm.loadGroup, data);

  return (
    <div className="group_destinations_modal">
      <CustomDialog
        onClose={close}
        open={open}
        className="group_destinations_modal__dialog"
        maxWidth="md"
        fullWidth
        PaperProps={{ elevation: 0, sx: { boxShadow: "0px 0px 10px #777" } }}
        slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" } } }}
      >
        <CustomDialogTitle
          className="group_destinations_modal__dialog__title"
          onClose={close}
        >
          Destination Groups
        </CustomDialogTitle>
        <DialogContent dividers className="group_destinations_modal__dialog__content">
          {/* DESTINATION GROUPS LIST */}
          <div className="group_destinations_list_container">
            {data.length > 0 ? (
              <List>
                {data.map((group_destination) => (
                  <ListItem
                    key={uuidv4()}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() =>
                          accordion.handleEditGroupDestination(
                            group_destination.group_name
                          )
                        }
                      >
                        <EditIcon />
                      </IconButton>
                    }
                  >
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                      >
                        <Typography sx={{ fontSize: "1.25rem", color: "#2d313a" }}>
                          {group_destination.group_name}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div className="start_list_view_container">
                          {(group_destination.alert_dest_list ?? []).length > 0 ? (
                            <List>
                              {(group_destination.alert_dest_list ?? []).map(
                                (destination: IAlertDestinations) => (
                                  <ListItem key={uuidv4()}>
                                    <ListItemAvatar>
                                      <Avatar sx={{ backgroundColor: "#0c1835" }}>
                                        <DestinationIcon type={destination.alert_dest_type} />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={destination.alert_dest_type}
                                      secondary={destination.value || null}
                                    />
                                  </ListItem>
                                )
                              )}
                            </List>
                          ) : (
                            <p className="start_list_view_none">
                              No Current Alert Destinations for this group.
                            </p>
                          )}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </ListItem>
                ))}
              </List>
            ) : (
              <p className="group_destinations_none">
                No Current Destination Groups. Use the fields below to add a new group.
              </p>
            )}
          </div>
          <Divider />
          <br />

          {/* ADD NEW GROUP ACCORDION */}
          <Accordion
            ref={accordion.accordionRef}
            expanded={accordion.accordionExpanded}
            onChange={() =>
              accordion.setAccordionExpanded(!accordion.accordionExpanded)
            }
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography sx={{ fontSize: "1.25rem", color: "#2d313a" }}>
                Add New Destination Group
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                className="group_destinations_textfield"
                label="Group Name"
                variant="standard"
                value={groupForm.form.group_name}
                name="group_name"
                onChange={groupForm.handleFormChange}
                fullWidth
                required
                error={groupForm.errors.group_name}
              />

              {slackChannels && telegramChannels && smsChannels && (
                <div className="start_list_view_container">
                  <p
                    className={
                      groupForm.errors.alert_dest_list
                        ? "start_list_view_container_error"
                        : ""
                    }
                  >
                    Alert Destinations
                  </p>

                  {(groupForm.form.alert_dest_list ?? []).length > 0 ? (
                    <List>
                      {(groupForm.form.alert_dest_list ?? []).map(
                        (destination: IAlertDestinations, idx: number) => (
                          <ListItem
                            key={uuidv4()}
                            secondaryAction={
                              <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={() =>
                                  groupForm.handleDeleteDestination(idx)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            }
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ backgroundColor: "#0c1835" }}>
                                <DestinationIcon type={destination.alert_dest_type} />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={destination.alert_dest_type}
                              secondary={destination.value || null}
                            />
                          </ListItem>
                        )
                      )}
                    </List>
                  ) : (
                    <p
                      className={`start_list_view_none ${
                        groupForm.errors.alert_dest_list
                          ? "start_list_view_container_error"
                          : ""
                      }`}
                    >
                      No Current Alert Destinations. Use the fields below to add a new
                      destination for this alert. Make sure to click on 'Save Destination'
                      to save your changes.
                    </p>
                  )}

                  <p className="pulse-typography-body-1 pulse-color--background-900 pulse-mb-1">
                    Add New Destination
                  </p>
                  <p className="pulse-typography-body-2 pulse-color--background-700 pulse-mb-1">
                    Please select your alert destination type. Ensure you 'Save Destination'.
                  </p>

                  <div className="new_list_textfields_container pulse-mb-1">
                    <TextField
                      className="start_list_view_textfield"
                      select
                      label="New Destination Type"
                      variant="standard"
                      value={destinationItem.newDestination.alert_dest_type}
                      name="alert_dest_type"
                      onChange={destinationItem.handleNewDestinationChange}
                      required
                      error={groupForm.errors.alert_dest_list}
                    >
                      {ALERT_DESTINATION_TYPES.map(({ label, value }: IAlertFormType) => (
                        <MenuItem key={uuidv4()} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>

                    {destinationItem.newDestination.alert_dest_type === "slack" &&
                      (slackChannels.length > 0 ? (
                        <TextField
                          className="start_list_view_textfield"
                          select
                          label="New Destination Location"
                          variant="standard"
                          value={destinationItem.newDestination.value}
                          onChange={destinationItem.handleNewDestinationChange}
                          name="value"
                        >
                          {slackChannels.map((channel) => (
                            <MenuItem key={uuidv4()} value={channel}>
                              {channel}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <p className="pulse-typography-body-2 pulse-color--background-800">
                          No Slack Channels Enabled
                        </p>
                      ))}

                    {destinationItem.newDestination.alert_dest_type === "telegram" &&
                      (telegramChannels.length > 0 ? (
                        <TextField
                          className="start_list_view_textfield"
                          select
                          label="New Destination Location"
                          variant="standard"
                          value={destinationItem.newDestination.value}
                          onChange={destinationItem.handleNewDestinationChange}
                          name="value"
                        >
                          {telegramChannels.map((channel) => (
                            <MenuItem key={uuidv4()} value={channel}>
                              {channel}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <p className="pulse-typography-body-2 pulse-color--background-800">
                          No Telegram Channels Enabled
                        </p>
                      ))}

                    {destinationItem.newDestination.alert_dest_type === "sms" &&
                      (smsChannels.length > 0 ? (
                        <TextField
                          className="start_list_view_textfield"
                          select
                          label="New Destination Location"
                          variant="standard"
                          value={destinationItem.newDestination.value}
                          onChange={destinationItem.handleNewDestinationChange}
                          name="value"
                        >
                          {smsChannels.map((channel) => (
                            <MenuItem key={uuidv4()} value={channel}>
                              {channel}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <p className="pulse-typography-body-2 pulse-color--background-800">
                          No SMS Channels Enabled
                        </p>
                      ))}

                    {destinationItem.newDestination.alert_dest_type === "file" && (
                      <TextField
                        className="start_list_view_textfield"
                        label="New Destination Location"
                        variant="standard"
                        name="value"
                        value={destinationItem.newDestination.value}
                        onChange={destinationItem.handleNewDestinationChange}
                      />
                    )}

                    {destinationItem.newDestination.alert_dest_type === "group" &&
                      (groupDestinationNames.length > 0 ? (
                        <TextField
                          className="start_list_view_textfield"
                          select
                          label="New Destination Location"
                          variant="standard"
                          value={destinationItem.newDestination.value}
                          onChange={destinationItem.handleNewDestinationChange}
                          name="value"
                        >
                          {groupDestinationNames.map((channel) => (
                            <MenuItem key={uuidv4()} value={channel}>
                              {channel}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <p className="pulse-typography-body-2 pulse-color--background-800">
                          No Group Destinations Enabled
                        </p>
                      ))}

                    <Button
                      className="start_list_view_button"
                      onClick={groupForm.handleAddDestination}
                      variant="outlined"
                      disabled={destinationItem.saveButtonDisabled}
                      startIcon={<SaveIcon />}
                      size="small"
                    >
                      Save Destination
                    </Button>
                  </div>
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions className="group_destinations_modal__dialog__actions">
          <Button
            autoFocus
            onClick={groupForm.handleSave}
            className="group_destinations_modal__dialog__actions_button"
            disabled={
              groupForm.pending ||
              groupForm.form.group_name.length < 1 ||
              (groupForm.form.alert_dest_list ?? []).length < 1
            }
          >
            {groupForm.pending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </CustomDialog>
    </div>
  );
};

export default GroupDestinationsModal;
